import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Children,
  createContext,
  FC,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { FieldItem, FieldLayout } from 'src/components/form/field-layout';
import { Label } from 'src/components/form/label';
import { Stack } from 'src/components/stack';
import { Tooltip } from 'src/components/tooltip';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useId } from 'src/hooks/use-id';
import styled from 'styled-components';

const Fields = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-gap: 1.5rem;
  align-items: start;
  grid-template-columns: 1fr 1fr;
`;

type FieldError = { name: string; showError: boolean };

type FieldGroupContextValue = {
  onError: (fieldError: FieldError) => void;
  id?: string;
} | null;

const FieldGroupContext = createContext<FieldGroupContextValue>(null);

export function useFieldGroup() {
  return useContext(FieldGroupContext);
}

type FieldGroupProps = {
  label: string;
  stacked?: boolean;
  stackedChildren?: boolean;
  id?: string;
  children: ReactNode;
  info?: ReactElement | string;
};

export const FieldGroup: FC<FieldGroupProps> = ({
  children,
  label,
  stacked = false,
  stackedChildren = false,
  id: givenId,
  info,
}) => {
  const { minTablet } = useBreakpoints();
  const generatedId = useId();
  const id = givenId ?? generatedId;
  const [errors, setErrors] = useState<FieldError[]>([]);
  const onError = useCallback(
    (fieldError: FieldError) =>
      setErrors((errors) =>
        errors
          .filter((error) => error.name !== fieldError.name)
          .concat([fieldError])
      ),
    []
  );

  const firstContextValue = useMemo(() => ({ onError, id }), [id, onError]);
  const otherContextValues = useMemo(
    () => ({ onError, id: undefined }),
    [onError]
  );

  const fields = Children.map(children, (child, index) => (
    <FieldGroupContext.Provider
      value={index === 0 ? firstContextValue : otherContextValues}
    >
      {child}
    </FieldGroupContext.Provider>
  ));

  return minTablet ? (
    <FieldLayout stacked={stacked}>
      <Label htmlFor={id} error={errors.some((error) => error.showError)}>
        {label}

        {info && (
          <>
            &nbsp;
            <Tooltip content={info}>
              {(targetProps) => (
                <FontAwesomeIcon {...targetProps} icon={faInfoCircle} />
              )}
            </Tooltip>
          </>
        )}
      </Label>

      {stackedChildren ? (
        <Stack gap={0.5}>{fields}</Stack>
      ) : (
        <FieldItem>
          <Fields>{fields}</Fields>
        </FieldItem>
      )}
    </FieldLayout>
  ) : (
    <Stack gap={1.5}>{fields}</Stack>
  );
};
